import type {
  AgentLocale,
  AgentTranslationOrigin,
  AiAgent,
  AiAgentTranslation,
  AiProposal,
  AiProviderInfo,
  AiRun,
  ApiToken,
  Asset,
  AssetFolder,
  AudioMode,
  BillingCheckoutSession,
  BillingPlan,
  BreakdownCategory,
  BreakdownItem,
  AudioProviderInfo,
  AuthSession,
  Character,
  Comment,
  ConceptBoard,
  Deliverable,
  DocumentBookmark,
  DocumentRecord,
  DocumentSummary,
  DocumentStats,
  EntityMergePreview,
  Episode,
  Integration,
  Location,
  CharacterLook,
  CharacterLookRole,
  CharacterModelRole,
  LocationModelRole,
  MediaGeneration,
  MediaProviderInfo,
  ModelAttachment,
  Notification,
  ProductionTask,
  Project,
  ProjectBreakdown,
  ProjectBundle,
  ProjectInvite,
  ProjectMember,
  ProjectPresence,
  ProjectReports,
  ProjectSearchResult,
  ProjectRole,
  Prop,
  PropUsageItem,
  ProseMirrorDoc,
  SearchResult,
  Shot,
  ShotComment,
  Snapshot,
  SnapshotDetail,
  StoryboardBatch,
  StoryboardRevision,
  StoryboardBoard,
  StoryboardBoardStatus,
  StructureNode,
  TitlePage,
  TranscriptionResult,
  TrashDependency,
  TrashEntityType,
  TrashItem,
  User,
  VoiceCastAssignment,
  VoiceCue,
  VoiceCueBatch,
  VoiceEmotion,
  VoiceProfile,
  Webhook,
  Workspace,
  WorkspaceInvite,
  WorkspaceInvitePreview,
  WorkspaceMember,
  WorkspaceRole,
  WorkspaceUsage,
  WritingDay,
} from "@korin/core";
import { useAuthStore } from "../store/auth";
import { localeTag, t } from "../i18n";
import { API_URL, apiUrl } from "./config";

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, message: string, code = "error", details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let refreshPromise: Promise<boolean> | null = null;

export async function refreshAuth(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const response = await fetch(apiUrl("/api/auth/refresh"), {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        useAuthStore.getState().clear();
        return false;
      }
      const session = (await response.json()) as AuthSession;
      useAuthStore.getState().setSession(session);
      return true;
    } catch {
      useAuthStore.getState().clear();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  retry?: boolean;
  raw?: boolean;
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return response.json();
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retry = true, raw, headers: customHeaders, ...rest } = options;
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(customHeaders);
  if (!(body instanceof FormData) && body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (token) headers.set("authorization", `Bearer ${token}`);
  headers.set("accept-language", localeTag());

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...rest,
      headers,
      credentials: "include",
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new ApiError(0, t("Нет связи с локальным бэкендом. Проверьте, запущен ли API."), "network", error);
  }

  if (response.status === 401 && retry) {
    const refreshed = await refreshAuth();
    if (refreshed) return request<T>(path, { ...options, retry: false });
    useAuthStore.getState().clear();
    throw new ApiError(401, t("Сессия истекла. Войдите снова."), "unauthorized");
  }

  if (!response.ok) {
    const data = (await parseResponse(response)) as {
      message?: string;
      error?: string;
      details?: unknown;
    } | null;
    throw new ApiError(
      response.status,
      data?.message ?? t("Ошибка {status}", { status: response.status }),
      data?.error ?? "error",
      data?.details,
    );
  }

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await parseResponse(response)) as T;
}

async function saveDownload(response: Response, fallbackName: string): Promise<void> {
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function bootAuth(): Promise<boolean> {
  useAuthStore.getState().setStatus("loading");
  const session = useAuthStore.getState();
  if (session.accessToken) {
    useAuthStore.getState().setStatus("authenticated");
    return true;
  }
  try {
    const response = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      useAuthStore.getState().clear();
      return false;
    }
    const data = (await response.json()) as AuthSession;
    useAuthStore.getState().setSession(data);
    return true;
  } catch {
    useAuthStore.getState().clear();
    return false;
  }
}

export async function login(identifier: string, password: string): Promise<AuthSession> {
  const response = await fetch(apiUrl("/api/auth/login"), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ identifier, password }),
  });
  const data = await parseResponse(response);
  if (!response.ok) {
    const err = data as { message?: string; error?: string };
    throw new ApiError(response.status, err?.message ?? t("Не удалось войти"), err?.error);
  }
  const session = data as AuthSession;
  useAuthStore.getState().setSession(session);
  return session;
}

export async function registerAccount(input: {
  name: string;
  email: string;
  password: string;
  workspaceName?: string;
  template?: "empty" | "film" | "series" | "audio";
  inviteToken?: string;
}): Promise<AuthSession> {
  const response = await fetch(apiUrl("/api/auth/register"), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await parseResponse(response);
  if (!response.ok) {
    const err = data as { message?: string; error?: string };
    throw new ApiError(response.status, err?.message ?? t("Не удалось создать аккаунт"), err?.error);
  }
  const session = data as AuthSession;
  useAuthStore.getState().setSession(session);
  return session;
}

export async function switchWorkspace(workspaceId: string): Promise<AuthSession> {
  const response = await fetch(apiUrl("/api/auth/switch"), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ workspaceId }),
  });
  const data = await parseResponse(response);
  if (!response.ok) {
    const err = data as { message?: string; error?: string };
    throw new ApiError(response.status, err?.message ?? t("Не удалось переключить workspace"), err?.error);
  }
  const session = data as AuthSession;
  useAuthStore.getState().setSession(session);
  return session;
}

export async function logout(): Promise<void> {
  try {
    await fetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" });
  } finally {
    useAuthStore.getState().clear();
  }
}

export interface PublicConfig {
  name: string;
  version: string;
  demoMode: boolean;
  allowDemoAccounts: boolean;
  storageMode: "local" | "s3";
  s3Configured: boolean;
  features: Record<string, boolean>;
  cloud?: {
    enabled: boolean;
    features?: Record<string, boolean>;
    workspaces?: boolean;
    registration?: boolean;
    inviteTtlDays?: number;
    billing?: { provider: string; configured: boolean; currency: string };
  };
  ai: { provider: string; model: string };
  media?: MediaProviderInfo;
  audio?: AudioProviderInfo;
  limits: { maxUploadMb: number };
}

export interface DemoAccount {
  id: string;
  name: string;
  email: string;
  phone?: string;
  title?: string;
  avatarColor: string;
  role: string;
  projectRole?: string;
  projectTitle?: string;
}

export async function getPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>("/api/config");
}

export async function getDemoAccounts(): Promise<{ enabled: boolean; password?: string; accounts: DemoAccount[] }> {
  return request("/api/auth/demo-accounts");
}

export interface DashboardProject extends Project {
  members?: ProjectMember[];
  progress?: number;
}

export interface DashboardData {
  stats: {
    projects: number;
    inProduction: number;
    members: number;
    words: number;
    scenes: number;
    assets: number;
    weeklyWords: number;
    activeWriters: number;
    myWeeklyWords: number;
    myWeeklyMinutes: number;
  };
  projects: DashboardProject[];
  activity: import("@korin/core").Activity[];
  writingDays: WritingDay[];
  deadlines: {
    type: "episode" | "deliverable";
    id: string;
    title: string;
    projectId: string;
    projectTitle: string;
    date: string;
    status: string;
  }[];
  unreadNotifications: number;
}

export async function getDashboard(): Promise<DashboardData> {
  return request("/api/dashboard");
}

export async function getProjects(archived?: "1" | "only"): Promise<DashboardProject[]> {
  return request(`/api/projects${archived ? `?archived=${archived}` : ""}`);
}

export async function getProject(projectId: string): Promise<ProjectBundle> {
  return request(`/api/projects/${projectId}`);
}

export async function getProjectPresence(projectId: string): Promise<ProjectPresence> {
  return request(`/api/projects/${projectId}/presence`);
}

export async function searchProject(projectId: string, query: string): Promise<ProjectSearchResult> {
  const params = new URLSearchParams({ q: query });
  return request(`/api/projects/${projectId}/search?${params.toString()}`);
}

export async function getProjectReports(projectId: string): Promise<ProjectReports> {
  return request(`/api/projects/${projectId}/reports`);
}

export async function getProjectBreakdown(projectId: string): Promise<ProjectBreakdown> {
  return request(`/api/projects/${projectId}/breakdown`);
}

export interface CreateBreakdownItemInput {
  documentId: string;
  sceneBlockId: string;
  category: BreakdownCategory;
  /** Optional when propId is set: the server snapshots the asset name. */
  name?: string;
  quantity?: number;
  note?: string;
  propId?: string | null;
}

export async function createBreakdownItem(
  projectId: string,
  input: CreateBreakdownItemInput,
): Promise<BreakdownItem> {
  return request(`/api/projects/${projectId}/breakdown`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateBreakdownItem(
  itemId: string,
  patch: Partial<Pick<BreakdownItem, "category" | "name" | "quantity" | "note" | "propId">>,
): Promise<BreakdownItem> {
  return request(`/api/breakdown/${itemId}`, {
    method: "PATCH",
    body: patch,
    retry: false,
  });
}

export async function deleteBreakdownItem(itemId: string): Promise<void> {
  await request(`/api/breakdown/${itemId}`, { method: "DELETE" });
}

/* -------------------------- presence SSE stream --------------------------- */

export type PresenceStreamStatus = "connecting" | "live" | "closed" | "error";

interface PresenceStreamFrame {
  event: string;
  data: string;
}

interface PresenceStreamListener {
  onPresence: (presence: ProjectPresence) => void;
  onStatus?: (status: PresenceStreamStatus) => void;
}

interface PresenceStreamEntry {
  controller: AbortController;
  listeners: Set<PresenceStreamListener>;
  status: PresenceStreamStatus;
  presence: ProjectPresence | null;
}

/**
 * One shared SSE connection per project. Several components (project nav,
 * overview, team) can watch presence at the same time; without this map every
 * hook instance would open its own stream. The connection lives until the last
 * subscriber unsubscribes.
 */
const presenceStreams = new Map<string, PresenceStreamEntry>();

/**
 * Incremental SSE frame parser. Chunks may split lines or whole frames, so
 * partial data stays in the buffer until a line/blank-line arrives. Heartbeat
 * comments (`: ping`) are ignored. Only `event: presence` frames are emitted by
 * the caller.
 */
function createPresenceFrameParser() {
  let buffer = "";
  let eventName = "";
  let dataLines: string[] = [];

  function takeLine(): string | null {
    const lf = buffer.indexOf("\n");
    const cr = buffer.indexOf("\r");
    if (lf === -1 && cr === -1) return null;
    if (cr !== -1 && (lf === -1 || cr < lf)) {
      // A trailing CR may be the first half of a CRLF split across chunks.
      if (cr === buffer.length - 1) return null;
      const line = buffer.slice(0, cr);
      buffer = buffer.slice(cr + (buffer[cr + 1] === "\n" ? 2 : 1));
      return line;
    }
    const line = buffer.slice(0, lf);
    buffer = buffer.slice(lf + 1);
    return line;
  }

  return {
    push(chunk: string): PresenceStreamFrame[] {
      buffer += chunk;
      const frames: PresenceStreamFrame[] = [];
      for (let line = takeLine(); line !== null; line = takeLine()) {
        if (line === "") {
          if (dataLines.length > 0) {
            frames.push({ event: eventName || "message", data: dataLines.join("\n") });
          }
          eventName = "";
          dataLines = [];
          continue;
        }
        if (line.startsWith(":")) continue;
        const colon = line.indexOf(":");
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? "" : line.slice(colon + 1);
        if (value.startsWith(" ")) value = value.slice(1);
        if (field === "event") eventName = value;
        else if (field === "data") dataLines.push(value);
      }
      return frames;
    },
  };
}

function parsePresenceFrame(frame: PresenceStreamFrame): ProjectPresence | null {
  if (frame.event !== "presence" && frame.event !== "message") return null;
  try {
    const value: unknown = JSON.parse(frame.data);
    if (!value || typeof value !== "object") return null;
    const record = value as Record<string, unknown>;
    if (
      !Array.isArray(record.users) ||
      typeof record.count !== "number" ||
      typeof record.updatedAt !== "string"
    ) {
      return null;
    }
    return value as ProjectPresence;
  } catch {
    return null;
  }
}

async function readPresenceStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onPresence: (presence: ProjectPresence) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createPresenceFrameParser();
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const frame of parser.push(chunk)) {
        const presence = parsePresenceFrame(frame);
        if (presence) onPresence(presence);
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // The body may already be cancelled by the abort signal.
    }
  }
}

function emitPresenceStatus(entry: PresenceStreamEntry, status: PresenceStreamStatus): void {
  entry.status = status;
  for (const listener of entry.listeners) {
    try {
      listener.onStatus?.(status);
    } catch {
      // A broken listener must not take down the shared stream.
    }
  }
}

function emitPresence(entry: PresenceStreamEntry, presence: ProjectPresence): void {
  entry.presence = presence;
  for (const listener of entry.listeners) {
    try {
      listener.onPresence(presence);
    } catch {
      // A broken listener must not take down the shared stream.
    }
  }
}

function presenceStreamUrl(projectId: string): string {
  return apiUrl(`/api/projects/${encodeURIComponent(projectId)}/presence/stream`);
}

async function openPresenceResponse(projectId: string, signal: AbortSignal): Promise<Response> {
  const token = useAuthStore.getState().accessToken;
  if (!token) throw new ApiError(401, t("Сессия истекла. Войдите снова."), "unauthorized");
  return fetch(presenceStreamUrl(projectId), {
    method: "GET",
    headers: {
      accept: "text/event-stream",
      "cache-control": "no-cache",
      authorization: `Bearer ${token}`,
    },
    credentials: "include",
    signal,
  });
}

async function runPresenceStream(projectId: string, entry: PresenceStreamEntry): Promise<void> {
  const signal = entry.controller.signal;
  try {
    emitPresenceStatus(entry, "connecting");

    let response: Response;
    try {
      response = await openPresenceResponse(projectId, signal);
    } catch {
      if (!signal.aborted) emitPresenceStatus(entry, "error");
      return;
    }

    if (response.status === 401) {
      // The access token may have expired between rendering and connecting.
      // Refresh once, retry once, then let the hook fall back to polling.
      const refreshed = await refreshAuth();
      if (signal.aborted) return;
      if (!refreshed) {
        emitPresenceStatus(entry, "error");
        return;
      }
      try {
        response = await openPresenceResponse(projectId, signal);
      } catch {
        if (!signal.aborted) emitPresenceStatus(entry, "error");
        return;
      }
      if (response.status === 401) {
        emitPresenceStatus(entry, "error");
        return;
      }
    }

    if (!response.ok || !response.body) {
      emitPresenceStatus(entry, "error");
      return;
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (contentType && !contentType.toLowerCase().includes("text/event-stream")) {
      emitPresenceStatus(entry, "error");
      return;
    }

    emitPresenceStatus(entry, "live");
    try {
      await readPresenceStream(response.body, signal, (presence) => emitPresence(entry, presence));
      if (!signal.aborted) emitPresenceStatus(entry, "closed");
    } catch {
      if (!signal.aborted) emitPresenceStatus(entry, "error");
    }
  } finally {
    if (presenceStreams.get(projectId) === entry) {
      presenceStreams.delete(projectId);
      entry.listeners.clear();
    }
  }
}

/**
 * Subscribe to project presence over SSE (the stream requires an Authorization
 * header, so `EventSource` cannot be used). Returns an unsubscribe function
 * that aborts the stream when the last subscriber leaves.
 *
 * `onStatus` reports transport state: connecting → live → closed/error. Auth
 * recovery is handled here (one refresh + one retry); ordinary reconnects are
 * the caller's job (`useProjectPresence` uses exponential backoff).
 */
export function subscribeProjectPresence(
  projectId: string,
  onPresence: (presence: ProjectPresence) => void,
  onStatus?: (status: PresenceStreamStatus) => void,
): () => void {
  const current = presenceStreams.get(projectId);
  // A terminal entry can linger for a tick between its last status and cleanup;
  // never let a new subscriber join a dead stream.
  const existing =
    current && current.status !== "closed" && current.status !== "error" ? current : undefined;
  const entry: PresenceStreamEntry =
    existing ?? {
      controller: new AbortController(),
      listeners: new Set<PresenceStreamListener>(),
      status: "connecting",
      presence: null,
    };
  if (!existing) {
    presenceStreams.set(projectId, entry);
    void runPresenceStream(projectId, entry);
  }

  const listener: PresenceStreamListener = { onPresence, onStatus };
  entry.listeners.add(listener);

  // A late subscriber may join an already live stream: replay the last snapshot
  // and status instead of waiting for the next awareness change.
  if (entry.presence) {
    try {
      onPresence(entry.presence);
    } catch {
      // A broken listener must not take down the shared stream.
    }
  }
  if (entry.status === "live" || entry.status === "connecting") {
    try {
      onStatus?.(entry.status);
    } catch {
      // A broken listener must not take down the shared stream.
    }
  }

  return () => {
    entry.listeners.delete(listener);
    if (presenceStreams.get(projectId) === entry && entry.listeners.size === 0) {
      presenceStreams.delete(projectId);
      entry.controller.abort();
    }
  };
}

export async function exportProjectArchive(
  projectId: string,
  options: { media?: boolean; formats?: string[] } = {},
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  if (options.media === false) params.set("media", "0");
  if (options.formats?.length) params.set("formats", options.formats.join(","));
  const query = params.toString();
  const response = await request<Response>(`/api/projects/${projectId}/export.zip${query ? `?${query}` : ""}`, {
    raw: true,
  });
  const disposition = response.headers.get("content-disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const ascii = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const filename = utf8 ? decodeURIComponent(utf8) : ascii ?? `korin-${projectId}.zip`;
  return { blob: await response.blob(), filename };
}

export async function createProject(input: {
  title: string;
  format: string;
  logline?: string;
  genre?: string;
  language?: string;
}): Promise<{ project: Project }> {
  return request("/api/projects", { method: "POST", body: input });
}

export async function updateProject(
  projectId: string,
  patch: Partial<{
    title: string;
    logline: string;
    synopsis: string;
    genre: string;
    status: string;
    tags: string[];
    cover: { color: string; accent: string; pattern: string };
  }>,
): Promise<Project> {
  return request(`/api/projects/${projectId}`, { method: "PATCH", body: patch });
}

export async function updateProjectSettings(
  projectId: string,
  settings: Record<string, unknown>,
): Promise<{ settings: Record<string, unknown> }> {
  return request(`/api/projects/${projectId}/settings`, { method: "PATCH", body: { settings } });
}

export async function archiveProject(projectId: string): Promise<void> {
  await request(`/api/projects/${projectId}`, { method: "DELETE" });
}

export async function restoreProject(projectId: string): Promise<Project> {
  return request(`/api/projects/${projectId}/restore`, { method: "POST" });
}

export async function purgeProject(
  projectId: string,
  confirmTitle: string,
): Promise<{
  deleted: true;
  title: string;
  assets: number;
  assetsDeleted: number;
  assetsFailed: number;
}> {
  return request(`/api/projects/${projectId}/purge`, {
    method: "POST",
    body: { confirmTitle },
  });
}

/* ------------------------------- documents -------------------------------- */

export interface DocumentResponse extends DocumentRecord {
  project: {
    id: string;
    title: string;
    format: string;
    settings: Record<string, unknown>;
  };
  updatedBy?: User;
}

export async function listDocuments(projectId: string): Promise<DocumentSummary[]> {
  return request(`/api/projects/${projectId}/documents`);
}

export async function getDocument(documentId: string): Promise<DocumentResponse> {
  return request(`/api/documents/${documentId}`);
}

export async function createDocument(
  projectId: string,
  input: { title: string; kind?: string; episodeNumber?: number; episodeId?: string; template?: boolean },
): Promise<DocumentSummary> {
  return request(`/api/projects/${projectId}/documents`, { method: "POST", body: input });
}

export async function updateDocument(
  documentId: string,
  patch: Partial<{
    title: string;
    status: string;
    titlePage: Partial<TitlePage>;
    settings: Record<string, unknown>;
    revision: string;
    content: ProseMirrorDoc;
  }>,
): Promise<DocumentRecord> {
  return request(`/api/documents/${documentId}`, { method: "PATCH", body: patch });
}

export interface ScriptEntityRef {
  id: string;
  name: string;
}

export interface MaterializeScriptResult {
  characters: ScriptEntityRef[];
  locations: ScriptEntityRef[];
  created: { characters: ScriptEntityRef[]; locations: ScriptEntityRef[] };
}

/**
 * STARC-style sync: the server reads the flushed screenplay and materializes
 * committed character cues and INT./EXT. locations. Called on a debounce while
 * typing; the response lets the editor refresh its bible only when names are
 * actually missing from the cached bundle.
 */
export async function materializeDocumentEntities(documentId: string): Promise<MaterializeScriptResult> {
  return request(`/api/documents/${documentId}/materialize`, { method: "POST" });
}

export interface EpisodeInput {
  season?: number;
  number?: number;
  title?: string;
  logline?: string;
  summary?: string;
  status?: Episode["status"];
  dueDate?: string | null;
  documentId?: string | null;
  budget?: number | null;
  budgetCurrency?: string;
  color?: string;
}

export async function createEpisode(projectId: string, input: EpisodeInput & { title: string }): Promise<Episode> {
  return request(`/api/projects/${projectId}/episodes`, { method: "POST", body: input });
}

export async function updateEpisode(episodeId: string, patch: EpisodeInput): Promise<Episode> {
  return request(`/api/episodes/${episodeId}`, { method: "PATCH", body: patch });
}

export async function deleteEpisode(episodeId: string): Promise<void> {
  await request(`/api/episodes/${episodeId}`, { method: "DELETE" });
}

export async function deleteDocument(documentId: string): Promise<void> {
  await request(`/api/documents/${documentId}`, { method: "DELETE" });
}

/* --------------------------------- trash ---------------------------------- */

export async function getProjectTrash(projectId: string): Promise<TrashItem[]> {
  return request(`/api/projects/${projectId}/trash`);
}

export async function restoreTrashItem(
  entityType: TrashEntityType,
  entityId: string,
): Promise<{ restored: boolean }> {
  return request(`/api/trash/${entityType}/${entityId}/restore`, { method: "POST" });
}

export async function purgeTrashItem(entityType: TrashEntityType, entityId: string): Promise<void> {
  await request(`/api/trash/${entityType}/${entityId}`, { method: "DELETE" });
}

export interface TrashPurgeResult {
  purged: number;
  failed: { entityType: TrashEntityType; entityId: string; title: string; message: string }[];
  retentionDays: number;
}

export async function purgeTrashBulk(
  projectId: string,
  options: { expiredOnly?: boolean } = {},
): Promise<TrashPurgeResult> {
  return request(`/api/projects/${projectId}/trash/purge`, {
    method: "POST",
    body: { expiredOnly: options.expiredOnly ?? false },
  });
}

export async function getTrashDependencies(
  entityType: TrashEntityType,
  entityId: string,
): Promise<{ dependencies: TrashDependency[] }> {
  return request(`/api/trash/${entityType}/${entityId}/dependencies`);
}

export async function importDocument(
  projectId: string,
  input: { format: "fountain" | "fdx" | "json"; content: string; title?: string },
): Promise<{ document: DocumentSummary; blocks: number }> {
  return request(`/api/projects/${projectId}/documents/import`, { method: "POST", body: input });
}

export type ScriptImportFormat = "docx" | "fountain" | "fdx" | "json";

export interface ScriptImportFile {
  filename: string;
  format?: ScriptImportFormat;
  data?: string;
  text?: string;
}

export interface ScriptImportEntityMatch {
  id: string;
  name: string;
}

export interface ScriptImportEntityPreview {
  key: string;
  kind: "character" | "location";
  variants: { name: string; count: number }[];
  matches: ScriptImportEntityMatch[];
  archived: ScriptImportEntityMatch[];
  suggestedName: string;
  suggestedId: string | null;
  needsChoice: boolean;
}

export interface ScriptImportDocumentDraft {
  id: string;
  filename: string;
  title: string;
  episodeNumber: number | null;
  content: ProseMirrorDoc;
  titlePage: TitlePage;
  warnings: string[];
  blocks: number;
  words: number;
  characters: string[];
  locations: string[];
}

export interface ScriptImportPreview {
  documents: ScriptImportDocumentDraft[];
  characters: ScriptImportEntityPreview[];
  locations: ScriptImportEntityPreview[];
  duplicates: {
    characters: { key: string; rows: ScriptImportEntityMatch[] }[];
    locations: { key: string; rows: ScriptImportEntityMatch[] }[];
  };
}

export interface ScriptImportChoice {
  key: string;
  name?: string;
  existingId?: string | null;
}

export interface ScriptImportResult {
  documents: DocumentSummary[];
  created: {
    characters: { id: string; name: string }[];
    locations: { id: string; name: string }[];
    scenes: { id: string; blockId: string; name: string }[];
  };
  warnings: string[];
  linkedEpisodes: number;
}

export async function previewScriptImport(
  projectId: string,
  files: ScriptImportFile[],
): Promise<ScriptImportPreview> {
  return request(`/api/projects/${projectId}/imports/preview`, { method: "POST", body: { files } });
}

export async function importScripts(
  projectId: string,
  input: {
    documents: {
      filename: string;
      title: string;
      episodeNumber?: number | null;
      content: ProseMirrorDoc;
      titlePage?: TitlePage;
    }[];
    characters?: ScriptImportChoice[];
    locations?: ScriptImportChoice[];
  },
): Promise<ScriptImportResult> {
  return request(`/api/projects/${projectId}/imports`, { method: "POST", body: input });
}

export async function reorderDocuments(projectId: string, documentIds: string[]): Promise<DocumentSummary[]> {
  return request(`/api/projects/${projectId}/documents/order`, { method: "PATCH", body: { documentIds } });
}

export async function previewMergeEntities(
  projectId: string,
  input: { kind: "character" | "location" | "prop"; sourceId: string; targetId: string },
): Promise<EntityMergePreview> {
  return request(`/api/projects/${projectId}/entities/merge/preview`, { method: "POST", body: input });
}

export async function mergeEntities(
  projectId: string,
  input: { kind: "character" | "location" | "prop"; sourceId: string; targetId: string },
): Promise<{
  scriptsChanged: number;
  assetsMoved: number;
  structureUpdated: number;
  voiceCuesMoved: number;
  /** Assets only: moved breakdown positions, tasks and 3D links. */
  breakdownItemsMoved?: number;
  tasksMoved?: number;
  modelsMoved?: number;
}> {
  return request(`/api/projects/${projectId}/entities/merge`, { method: "POST", body: input });
}

export async function downloadDocument(documentId: string, format: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(apiUrl(`/api/documents/${documentId}/export?format=${format}`), {
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    credentials: "include",
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, data?.message ?? t("Не удалось экспортировать"));
  }
  await saveDownload(response, `document.${format}`);
}

/* -------------------------------- versions -------------------------------- */

export async function getVersions(documentId: string): Promise<Snapshot[]> {
  return request(`/api/documents/${documentId}/versions`);
}

export async function createVersion(
  documentId: string,
  input: { name: string; note?: string },
): Promise<Snapshot> {
  return request(`/api/documents/${documentId}/versions`, { method: "POST", body: input });
}

export async function getVersion(versionId: string): Promise<SnapshotDetail> {
  return request(`/api/versions/${versionId}`);
}

export async function restoreVersion(versionId: string): Promise<{ document: DocumentRecord }> {
  return request(`/api/versions/${versionId}/restore`, { method: "POST" });
}

/* -------------------------------- comments -------------------------------- */

export async function getComments(documentId: string): Promise<Comment[]> {
  return request(`/api/documents/${documentId}/comments`);
}

export async function addComment(
  documentId: string,
  input: {
    body: string;
    blockId?: string | null;
    parentId?: string | null;
    rangeStart?: number;
    rangeEnd?: number;
    quote?: string;
    mentions?: string[];
  },
): Promise<Comment> {
  return request(`/api/documents/${documentId}/comments`, { method: "POST", body: input });
}

export async function updateComment(
  commentId: string,
  input: { body?: string; resolved?: boolean },
): Promise<Comment> {
  return request(`/api/comments/${commentId}`, { method: "PATCH", body: input });
}

export async function deleteComment(commentId: string): Promise<void> {
  await request(`/api/comments/${commentId}`, { method: "DELETE" });
}

/* -------------------------------- bookmarks -------------------------------- */

export async function getBookmarks(documentId: string): Promise<DocumentBookmark[]> {
  return request(`/api/documents/${documentId}/bookmarks`);
}

export async function createBookmark(
  documentId: string,
  input: {
    blockId: string;
    rangeStart?: number;
    rangeEnd?: number;
    quote?: string;
    label?: string;
  },
): Promise<DocumentBookmark> {
  return request(`/api/documents/${documentId}/bookmarks`, { method: "POST", body: input });
}

export async function updateBookmark(bookmarkId: string, label: string): Promise<DocumentBookmark> {
  return request(`/api/bookmarks/${bookmarkId}`, { method: "PATCH", body: { label } });
}

export async function deleteBookmark(bookmarkId: string): Promise<void> {
  await request(`/api/bookmarks/${bookmarkId}`, { method: "DELETE" });
}

/* -------------------------------- bibles ---------------------------------- */

export async function getCharacters(projectId: string): Promise<Character[]> {
  return request(`/api/projects/${projectId}/characters`);
}

export async function createCharacter(projectId: string, input: Partial<Character> & { name: string }): Promise<Character> {
  return request(`/api/projects/${projectId}/characters`, { method: "POST", body: input });
}

export async function updateCharacter(characterId: string, patch: Partial<Character>): Promise<Character> {
  return request(`/api/characters/${characterId}`, { method: "PATCH", body: patch });
}

export async function deleteCharacter(characterId: string): Promise<void> {
  await request(`/api/characters/${characterId}`, { method: "DELETE" });
}

export type BibleCardFormat = "pdf" | "docx";

export async function downloadCharacterCard(characterId: string, format: BibleCardFormat): Promise<void> {
  const response = await request<Response>(`/api/characters/${characterId}/export?format=${format}`, { raw: true });
  await saveDownload(response, `character.${format}`);
}

export async function downloadLocationCard(locationId: string, format: BibleCardFormat): Promise<void> {
  const response = await request<Response>(`/api/locations/${locationId}/export?format=${format}`, { raw: true });
  await saveDownload(response, `location.${format}`);
}

export type BibleArchiveType = "all" | "characters" | "locations";

/** Downloads the whole bible (or one entity kind) as one server-built ZIP. */
export async function downloadBibleArchive(
  projectId: string,
  format: BibleCardFormat,
  type: BibleArchiveType = "all",
): Promise<void> {
  const response = await request<Response>(
    `/api/projects/${projectId}/bible/export?format=${format}&type=${type}`,
    { raw: true },
  );
  await saveDownload(response, `bible-${type}.zip`);
}

export async function getLocations(projectId: string): Promise<Location[]> {
  return request(`/api/projects/${projectId}/locations`);
}

export async function createLocation(projectId: string, input: Partial<Location> & { name: string }): Promise<Location> {
  return request(`/api/projects/${projectId}/locations`, { method: "POST", body: input });
}

export async function updateLocation(locationId: string, patch: Partial<Location>): Promise<Location> {
  return request(`/api/locations/${locationId}`, { method: "PATCH", body: patch });
}

export async function deleteLocation(locationId: string): Promise<void> {
  await request(`/api/locations/${locationId}`, { method: "DELETE" });
}

/* --------------------------------- props ---------------------------------- */

/** Writable prop fields; the server derives preview and timestamps. */
export type PropWriteInput = Partial<
  Omit<
    Prop,
    "id" | "projectId" | "createdAt" | "updatedAt" | "imageUrl" | "previewAssetId" | "previewSource"
  >
>;

export type PropInput = PropWriteInput & { name: string };

export async function listProps(projectId: string): Promise<Prop[]> {
  return request(`/api/projects/${projectId}/props`);
}

export async function createProp(projectId: string, input: PropInput): Promise<Prop> {
  return request(`/api/projects/${projectId}/props`, { method: "POST", body: input });
}

export async function updateProp(propId: string, patch: PropWriteInput): Promise<Prop> {
  return request(`/api/props/${propId}`, { method: "PATCH", body: patch });
}

export async function deleteProp(propId: string): Promise<void> {
  await request(`/api/props/${propId}`, { method: "DELETE" });
}

export async function restoreProp(propId: string): Promise<Prop> {
  return request(`/api/props/${propId}/restore`, { method: "POST" });
}

/** Reverse link: active breakdown positions that reference the asset. */
export interface PropUsage {
  propId: string;
  items: PropUsageItem[];
}

export async function getPropUsage(propId: string): Promise<PropUsage> {
  return request(`/api/props/${propId}/usage`);
}

/** Production tasks about the asset, for the card's "Задачи" block. */
export interface PropTasks {
  propId: string;
  tasks: ProductionTask[];
}

export async function getPropTasks(propId: string): Promise<PropTasks> {
  return request(`/api/props/${propId}/tasks`);
}

export async function createStructureNode(projectId: string, input: Partial<StructureNode> & { title: string; kind: string }): Promise<StructureNode> {
  return request(`/api/projects/${projectId}/structure`, { method: "POST", body: input });
}

export async function updateStructureNode(nodeId: string, patch: Partial<StructureNode>): Promise<StructureNode> {
  return request(`/api/structure/${nodeId}`, { method: "PATCH", body: patch });
}

export async function deleteStructureNode(nodeId: string): Promise<void> {
  await request(`/api/structure/${nodeId}`, { method: "DELETE" });
}

export interface SceneMergeResult {
  sourceId: string;
  targetId: string;
  archiveBatchId: string;
  moved: {
    boards: number;
    shots: number;
    voiceCues: number;
    tasks: number;
    breakdownItems: number;
    assets: number;
    children: number;
  };
}

export async function mergeScene(sourceId: string, targetId: string): Promise<SceneMergeResult> {
  return request(`/api/structure/${sourceId}/merge`, { method: "POST", body: { targetId } });
}

export interface ArchivedSceneRef {
  id: string;
  title: string;
  documentId: string | null;
  sceneBlockId: string | null;
  archivedAt: string;
  archiveBatchId: string | null;
}

export async function getArchivedScenes(projectId: string, documentId?: string): Promise<ArchivedSceneRef[]> {
  const query = documentId ? "?documentId=" + encodeURIComponent(documentId) : "";
  return request(`/api/projects/${projectId}/scenes/archived${query}`);
}

export async function backfillProjectScenes(projectId: string): Promise<{
  documents: number;
  createdScenes: number;
  createdCharacters: number;
  createdLocations: number;
  archivedScenes: number;
}> {
  return request(`/api/projects/${projectId}/scenes/backfill`, { method: "POST" });
}

export async function reorderStructure(
  projectId: string,
  order: { id: string; parentId?: string | null; order: number }[],
): Promise<void> {
  await request(`/api/projects/${projectId}/structure/reorder`, { method: "POST", body: { order } });
}

/* ---------------------------------- AI ------------------------------------ */

export async function getAiAgents(projectId: string): Promise<AiAgent[]> {
  return request(`/api/projects/${projectId}/ai/agents`);
}

export async function createAiAgent(
  projectId: string,
  input: {
    template: string;
    locale?: AgentLocale;
    name?: string;
    role?: string;
    tagline?: string;
    persona?: string;
    instructions?: string;
    model?: string;
    color?: string;
    emoji?: string;
  },
): Promise<AiAgent> {
  return request(`/api/projects/${projectId}/ai/agents`, { method: "POST", body: input });
}

export async function updateAiAgent(agentId: string, patch: Partial<AiAgent>): Promise<AiAgent> {
  return request(`/api/ai/agents/${agentId}`, { method: "PATCH", body: patch });
}

export async function deleteAiAgent(agentId: string): Promise<void> {
  await request(`/api/ai/agents/${agentId}`, { method: "DELETE" });
}

/** A translation draft: previewed in the modal, persisted only by the human. */
export interface AgentTranslationDraft {
  name: string;
  role: string;
  tagline: string;
  persona: string;
  instructions: string;
  locale: AgentLocale;
  origin: AgentTranslationOrigin;
  sourceLocale: AgentLocale;
  presetFallback: boolean;
  model?: string;
}

export async function getAiAgentTranslations(agentId: string): Promise<AiAgentTranslation[]> {
  return request(`/api/ai/agents/${agentId}/translations`);
}

export async function translateAiAgent(agentId: string, locale: AgentLocale): Promise<AgentTranslationDraft> {
  return request(`/api/ai/agents/${agentId}/translate`, { method: "POST", body: { locale } });
}

export async function saveAiAgentTranslation(
  agentId: string,
  locale: AgentLocale,
  input: {
    name: string;
    role: string;
    tagline: string;
    persona: string;
    instructions: string;
    origin?: AgentTranslationOrigin;
    makeWorkLocale?: boolean;
  },
): Promise<AiAgentTranslation> {
  return request(`/api/ai/agents/${agentId}/translations/${locale}`, { method: "PUT", body: input });
}

export async function deleteAiAgentTranslation(agentId: string, locale: AgentLocale): Promise<void> {
  await request(`/api/ai/agents/${agentId}/translations/${locale}`, { method: "DELETE" });
}

export async function getAiProviders(): Promise<AiProviderInfo[]> {
  return request("/api/ai/providers");
}

export async function runWritersRoom(
  projectId: string,
  input: { agentIds: string[]; brief: string; focus?: string; documentId?: string; blockId?: string },
): Promise<{ runId: string; proposals: AiProposal[] }> {
  return request(`/api/projects/${projectId}/ai/run`, { method: "POST", body: input });
}

export async function getAiProposals(projectId: string, status: string = "pending"): Promise<AiProposal[]> {
  return request(`/api/projects/${projectId}/ai/proposals?status=${status}`);
}

export async function getAiRuns(projectId: string): Promise<AiRun[]> {
  return request(`/api/projects/${projectId}/ai/runs`);
}

export async function applyAiProposal(
  proposalId: string,
): Promise<{ proposal: AiProposal; content?: ProseMirrorDoc; characterId?: string; document?: DocumentRecord; voiceCueIds?: string[] }> {
  return request(`/api/ai/proposals/${proposalId}/apply`, { method: "POST" });
}

export async function rejectAiProposal(proposalId: string): Promise<AiProposal> {
  return request(`/api/ai/proposals/${proposalId}/reject`, { method: "POST" });
}

export async function deleteAiProposal(proposalId: string): Promise<void> {
  await request(`/api/ai/proposals/${proposalId}`, { method: "DELETE" });
}

/* -------------------------------- assets ---------------------------------- */

export async function getAssets(
  projectId: string,
  params: { kind?: string; q?: string; folderId?: string } = {},
): Promise<Asset[]> {
  const query = new URLSearchParams();
  if (params.kind && params.kind !== "all") query.set("kind", params.kind);
  if (params.q) query.set("q", params.q);
  if (params.folderId && params.folderId !== "all") query.set("folderId", params.folderId);
  const suffix = query.size ? `?${query.toString()}` : "";
  return request(`/api/projects/${projectId}/assets${suffix}`);
}

export async function getAssetFolders(projectId: string): Promise<AssetFolder[]> {
  return request(`/api/projects/${projectId}/asset-folders`);
}

export async function createAssetFolder(
  projectId: string,
  input: { name: string; parentId?: string | null },
): Promise<AssetFolder> {
  return request(`/api/projects/${projectId}/asset-folders`, { method: "POST", body: input });
}

export async function updateAssetFolder(
  folderId: string,
  patch: { name?: string; parentId?: string | null },
): Promise<AssetFolder> {
  return request(`/api/asset-folders/${folderId}`, { method: "PATCH", body: patch });
}

export async function deleteAssetFolder(folderId: string): Promise<void> {
  await request(`/api/asset-folders/${folderId}`, { method: "DELETE" });
}

export async function moveAsset(
  assetId: string,
  input: { folderId: string | null },
): Promise<Asset> {
  return request(`/api/assets/${assetId}`, { method: "PATCH", body: input });
}

/** MIME the API expects for .glb assets. */
const GLB_MIME = "model/gltf-binary";

export async function uploadAsset(
  projectId: string,
  file: File,
  options: { tags?: string[]; linkedEntityType?: string; linkedEntityId?: string; folderId?: string } = {},
): Promise<Asset> {
  const form = new FormData();
  // Browsers usually leave File.type empty for .glb (or fall back to
  // application/octet-stream), and the multipart part mime is the only hint the
  // API gets. Name it explicitly; Content-Type of the whole request stays with
  // the browser so it can append the multipart boundary.
  const upload =
    /\.glb$/i.test(file.name) && file.type !== GLB_MIME
      ? new File([file], file.name, { type: GLB_MIME, lastModified: file.lastModified })
      : file;
  form.append("file", upload, upload.name);
  const query = new URLSearchParams();
  if (options.tags?.length) query.set("tags", options.tags.join(","));
  if (options.linkedEntityType) query.set("linkedEntityType", options.linkedEntityType);
  if (options.linkedEntityId) query.set("linkedEntityId", options.linkedEntityId);
  if (options.folderId) query.set("folderId", options.folderId);
  const suffix = query.size ? `?${query.toString()}` : "";
  return request(`/api/projects/${projectId}/assets${suffix}`, {
    method: "POST",
    body: form,
    retry: false,
  });
}

export async function deleteAsset(assetId: string): Promise<void> {
  await request(`/api/assets/${assetId}`, { method: "DELETE" });
}

/* ----------------------------- 3D model links ----------------------------- */

export interface AttachModelInput {
  assetId: string;
  lookId?: string | null;
  role?: CharacterModelRole | LocationModelRole;
  note?: string;
}

export interface UpdateModelInput {
  role?: CharacterModelRole | LocationModelRole;
  note?: string;
  lookId?: string | null;
}

export async function listCharacterModels(characterId: string): Promise<ModelAttachment[]> {
  return request(`/api/characters/${characterId}/models`);
}

export async function attachCharacterModel(
  characterId: string,
  input: AttachModelInput,
): Promise<ModelAttachment> {
  return request(`/api/characters/${characterId}/models`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateCharacterModel(
  characterId: string,
  modelId: string,
  patch: UpdateModelInput,
): Promise<ModelAttachment> {
  return request(`/api/characters/${characterId}/models/${modelId}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function detachCharacterModel(characterId: string, modelId: string): Promise<void> {
  await request(`/api/characters/${characterId}/models/${modelId}`, { method: "DELETE" });
}

export async function listLocationModels(locationId: string): Promise<ModelAttachment[]> {
  return request(`/api/locations/${locationId}/models`);
}

export async function attachLocationModel(
  locationId: string,
  input: AttachModelInput,
): Promise<ModelAttachment> {
  return request(`/api/locations/${locationId}/models`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateLocationModel(
  locationId: string,
  modelId: string,
  patch: UpdateModelInput,
): Promise<ModelAttachment> {
  return request(`/api/locations/${locationId}/models/${modelId}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function detachLocationModel(locationId: string, modelId: string): Promise<void> {
  await request(`/api/locations/${locationId}/models/${modelId}`, { method: "DELETE" });
}

export async function listPropModels(propId: string): Promise<ModelAttachment[]> {
  return request(`/api/props/${propId}/models`);
}

export async function attachPropModel(propId: string, input: AttachModelInput): Promise<ModelAttachment> {
  return request(`/api/props/${propId}/models`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updatePropModel(
  propId: string,
  modelId: string,
  patch: UpdateModelInput,
): Promise<ModelAttachment> {
  return request(`/api/props/${propId}/models/${modelId}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function detachPropModel(propId: string, modelId: string): Promise<void> {
  await request(`/api/props/${propId}/models/${modelId}`, { method: "DELETE" });
}

/* --------------------------- media generation ----------------------------- */

export interface CreateGenerationInput {
  kind: "image" | "video";
  purpose: "storyboard" | "character" | "location" | "mood";
  prompt: string;
  negativePrompt?: string;
  aspectRatio: string;
  resolution: string;
  duration?: number;
  linkedEntityType?: "character" | "location" | "scene" | null;
  linkedEntityId?: string | null;
  styleId?: string;
  references?: { entityType: "character" | "location"; entityId: string }[];
  /** Character look whose images become the identity references. */
  lookId?: string;
}

export async function getGenerations(projectId: string): Promise<MediaGeneration[]> {
  return request(`/api/projects/${projectId}/generations`);
}

export async function createGeneration(projectId: string, input: CreateGenerationInput): Promise<MediaGeneration> {
  return request(`/api/projects/${projectId}/generations`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export interface BatchShotInput {
  label: string;
  prompt: string;
}

export interface CreateGenerationBatchInput {
  title?: string;
  style?: string;
  shots: BatchShotInput[];
  aspectRatio: string;
  resolution: string;
  linkedEntityId: string;
  negativePrompt?: string;
}

export async function createGenerationBatch(
  projectId: string,
  input: CreateGenerationBatchInput,
): Promise<{ batchId: string; board: StoryboardBoard; generations: MediaGeneration[] }> {
  return request(`/api/projects/${projectId}/generations/batch`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export interface CreateProjectStoryboardBatchInput {
  title?: string;
  sceneIds: string[];
  shotsPerScene: number;
  style?: string;
  aspectRatio: string;
  resolution: string;
  negativePrompt?: string;
}

export async function createProjectStoryboardBatch(
  projectId: string,
  input: CreateProjectStoryboardBatchInput,
): Promise<StoryboardBatch> {
  return request(`/api/projects/${projectId}/generations/storyboard-batch`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function getProjectStoryboardBatch(
  projectId: string,
  batchId: string,
): Promise<StoryboardBatch> {
  return request(`/api/projects/${projectId}/generations/storyboard-batch/${encodeURIComponent(batchId)}`, {
    retry: false,
  });
}

export async function cancelProjectStoryboardBatch(
  projectId: string,
  batchId: string,
  sceneIds?: string[],
): Promise<StoryboardBatch> {
  return request(`/api/projects/${projectId}/generations/storyboard-batch/${encodeURIComponent(batchId)}/cancel`, {
    method: "POST",
    body: sceneIds?.length ? { sceneIds } : {},
    retry: false,
  });
}

export async function retryProjectStoryboardBatch(
  projectId: string,
  batchId: string,
  sceneIds?: string[],
): Promise<StoryboardBatch> {
  return request(`/api/projects/${projectId}/generations/storyboard-batch/${encodeURIComponent(batchId)}/retry`, {
    method: "POST",
    body: sceneIds?.length ? { sceneIds } : {},
    retry: false,
  });
}

/**
 * One frame is one human action: cancelling a single take must not stop the
 * rest of the batch. The server returns the whole batch, so the dialog can
 * replace its cache entry without a second read.
 */
export async function cancelProjectStoryboardFrame(
  projectId: string,
  batchId: string,
  generationId: string,
): Promise<StoryboardBatch> {
  return request(
    `/api/projects/${projectId}/generations/storyboard-batch/${encodeURIComponent(batchId)}/frames/${encodeURIComponent(generationId)}/cancel`,
    { method: "POST", retry: false },
  );
}

/** Re-queues exactly one failed or cancelled take inside its existing shot. */
export async function retryProjectStoryboardFrame(
  projectId: string,
  batchId: string,
  generationId: string,
): Promise<StoryboardBatch> {
  return request(
    `/api/projects/${projectId}/generations/storyboard-batch/${encodeURIComponent(batchId)}/frames/${encodeURIComponent(generationId)}/retry`,
    { method: "POST", retry: false },
  );
}

export async function getStoryboardBoards(projectId: string, sceneId?: string): Promise<StoryboardBoard[]> {
  const query = sceneId ? `?sceneId=${encodeURIComponent(sceneId)}` : "";
  return request(`/api/projects/${projectId}/storyboard-boards${query}`);
}

export async function createStoryboardBoard(
  projectId: string,
  input: {
    sceneId: string;
    sceneBlockId?: string;
    title?: string;
    source?: "generated" | "imported";
    styleId?: string;
    style?: string;
  },
): Promise<StoryboardBoard> {
  return request(`/api/projects/${projectId}/storyboard-boards`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateStoryboardBoard(
  boardId: string,
  input: {
    title?: string;
    status?: StoryboardBoardStatus;
    isPrimary?: boolean;
    sortOrder?: number;
  },
): Promise<StoryboardBoard> {
  return request(`/api/storyboard-boards/${boardId}`, {
    method: "PATCH",
    body: input,
    retry: false,
  });
}

export async function moveStoryboardBoard(
  boardId: string,
  direction: "up" | "down",
): Promise<StoryboardBoard> {
  return request(`/api/storyboard-boards/${boardId}/move`, {
    method: "POST",
    body: { direction },
    retry: false,
  });
}

export async function getStoryboardShots(boardId: string): Promise<Shot[]> {
  return request(`/api/storyboard-boards/${encodeURIComponent(boardId)}/shots`);
}

export async function getSceneShots(projectId: string, sceneId: string): Promise<Shot[]> {
  return request(`/api/projects/${encodeURIComponent(projectId)}/storyboard-shots?sceneNodeId=${encodeURIComponent(sceneId)}`);
}

export async function createStoryboardShot(
  boardId: string,
  input: { label: string; notes?: string },
): Promise<Shot> {
  return request(`/api/storyboard-boards/${encodeURIComponent(boardId)}/shots`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function getStoryboardShot(shotId: string): Promise<Shot> {
  return request(`/api/shots/${encodeURIComponent(shotId)}`);
}

export async function updateStoryboardShot(
  shotId: string,
  input: { label?: string; notes?: string },
): Promise<Shot> {
  return request(`/api/shots/${encodeURIComponent(shotId)}`, {
    method: "PATCH",
    body: input,
    retry: false,
  });
}

export async function selectStoryboardShotGeneration(shotId: string, generationId: string): Promise<Shot> {
  return request(`/api/shots/${encodeURIComponent(shotId)}/select`, {
    method: "POST",
    body: { generationId },
    retry: false,
  });
}

export async function deleteStoryboardShot(shotId: string): Promise<void> {
  await request(`/api/shots/${encodeURIComponent(shotId)}`, { method: "DELETE", retry: false });
}

export async function deleteStoryboardBoard(boardId: string): Promise<void> {
  await request(`/api/storyboard-boards/${encodeURIComponent(boardId)}`, { method: "DELETE", retry: false });
}

export async function getStoryboardShotRevisions(shotId: string): Promise<StoryboardRevision[]> {
  return request(`/api/shots/${encodeURIComponent(shotId)}/revisions`);
}

export async function createStoryboardShotRevision(shotId: string, note?: string): Promise<StoryboardRevision> {
  return request(`/api/shots/${encodeURIComponent(shotId)}/revisions`, {
    method: "POST",
    body: { note },
    retry: false,
  });
}

export async function getStoryboardBoardRevisions(boardId: string): Promise<StoryboardRevision[]> {
  return request(`/api/storyboard-boards/${encodeURIComponent(boardId)}/revisions`);
}

export async function createStoryboardBoardRevision(boardId: string, note?: string): Promise<StoryboardRevision> {
  return request(`/api/storyboard-boards/${encodeURIComponent(boardId)}/revisions`, {
    method: "POST",
    body: { note },
    retry: false,
  });
}

export async function restoreStoryboardRevision(revisionId: string): Promise<{ restored: true; targetType: string; targetId: string }> {
  return request(`/api/storyboard-revisions/${encodeURIComponent(revisionId)}/restore`, {
    method: "POST",
    retry: false,
  });
}

export async function getShotComments(shotId: string): Promise<ShotComment[]> {
  return request(`/api/shots/${encodeURIComponent(shotId)}/comments`);
}

export async function createShotComment(
  shotId: string,
  input: { body: string; mentions?: string[] },
): Promise<ShotComment> {
  return request(`/api/shots/${encodeURIComponent(shotId)}/comments`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateShotComment(
  commentId: string,
  input: { body?: string; resolved?: boolean },
): Promise<ShotComment> {
  return request(`/api/shot-comments/${encodeURIComponent(commentId)}`, {
    method: "PATCH",
    body: input,
    retry: false,
  });
}

export async function deleteShotComment(commentId: string): Promise<void> {
  await request(`/api/shot-comments/${encodeURIComponent(commentId)}`, { method: "DELETE", retry: false });
}

export async function applyGeneration(
  generationId: string,
  target: { targetType?: "character" | "location" | "scene"; targetId?: string } = {},
): Promise<MediaGeneration> {
  return request(`/api/generations/${generationId}/apply`, {
    method: "POST",
    body: target,
    retry: false,
  });
}

export async function retryGenerationStorage(generationId: string): Promise<MediaGeneration> {
  return request(`/api/generations/${generationId}/retry-storage`, {
    method: "POST",
    retry: false,
  });
}

export async function deleteGeneration(generationId: string): Promise<void> {
  await request(`/api/generations/${generationId}`, { method: "DELETE" });
}

export async function importStoryboardFrame(
  projectId: string,
  input: { assetId: string; sceneId: string; shotLabel?: string; boardId?: string; batchId?: string; storyboardTitle?: string },
): Promise<MediaGeneration> {
  return request<MediaGeneration>(`/api/projects/${projectId}/generations/import-storyboard`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

/* --------------------------- voice / audio / lip-sync --------------------- */

export async function getAudioProvider(): Promise<AudioProviderInfo> {
  return request<AudioProviderInfo>("/api/audio/provider");
}

export async function getVoiceProfiles(projectId: string): Promise<VoiceProfile[]> {
  return request<VoiceProfile[]>(`/api/projects/${projectId}/voice-profiles`);
}

export interface CreateVoiceProfileInput {
  name: string;
  description?: string;
  language?: string;
  characterId?: string;
  emotion?: VoiceEmotion;
  referenceTranscript: string;
  isDefault?: boolean;
  sampleAssetIds: string[];
  consent: true;
}

export async function createVoiceProfile(projectId: string, input: CreateVoiceProfileInput): Promise<VoiceProfile> {
  return request<VoiceProfile>(`/api/projects/${projectId}/voice-profiles`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function deleteVoiceProfile(profileId: string): Promise<void> {
  await request(`/api/voice-profiles/${profileId}`, { method: "DELETE" });
}

export async function transcribeVoiceSample(
  projectId: string,
  sampleAssetId: string,
  language = "ru",
): Promise<TranscriptionResult> {
  return request<TranscriptionResult>(`/api/projects/${projectId}/voice-profiles/transcribe`, {
    method: "POST",
    body: { sampleAssetId, language },
    retry: false,
  });
}

export interface CreateAudioGenerationInput {
  mode: AudioMode;
  text?: string;
  prompt?: string;
  language?: string;
  voiceProfileId?: string;
  speed?: number;
  duration?: number;
  instrumental?: boolean;
  format?: "wav" | "mp3";
  cueId?: string;
}

export async function getAudioGenerations(projectId: string): Promise<MediaGeneration[]> {
  return request<MediaGeneration[]>(`/api/projects/${projectId}/audio/generations`);
}

export async function createAudioGeneration(
  projectId: string,
  input: CreateAudioGenerationInput,
): Promise<MediaGeneration> {
  return request<MediaGeneration>(`/api/projects/${projectId}/audio/generations`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export interface CreateLipSyncInput {
  videoAssetId: string;
  audioAssetId: string;
  prompt?: string;
  quality?: "standard" | "high";
}

export async function createLipSync(projectId: string, input: CreateLipSyncInput): Promise<MediaGeneration> {
  return request<MediaGeneration>(`/api/projects/${projectId}/lipsync`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export interface CreateTalkingVideoInput {
  imageAssetId: string;
  audioAssetId: string;
  prompt?: string;
  quality?: "standard" | "high";
  characterId?: string;
  voiceCueId?: string;
}

export async function createTalkingVideo(
  projectId: string,
  input: CreateTalkingVideoInput,
): Promise<MediaGeneration> {
  return request<MediaGeneration>(`/api/projects/${projectId}/talking-video`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export interface VoiceCueInput {
  cueId?: string;
  sceneNodeId: string;
  shotId?: string | null;
  shotKey?: string;
  shotLabel?: string;
  characterId: string;
  voiceProfileId: string;
  emotion?: VoiceEmotion;
  text?: string;
}

export async function getVoiceCues(
  projectId: string,
  filters: { documentId?: string; sceneNodeId?: string; characterId?: string } = {},
): Promise<VoiceCue[]> {
  const query = new URLSearchParams();
  if (filters.documentId) query.set("documentId", filters.documentId);
  if (filters.sceneNodeId) query.set("sceneNodeId", filters.sceneNodeId);
  if (filters.characterId) query.set("characterId", filters.characterId);
  return request<VoiceCue[]>(`/api/projects/${projectId}/voice-cues${query.size ? `?${query.toString()}` : ""}`);
}

export async function saveVoiceCue(projectId: string, input: VoiceCueInput): Promise<VoiceCue> {
  return request<VoiceCue>(`/api/projects/${projectId}/voice-cues`, {
    method: "PUT",
    body: input,
    retry: false,
  });
}

export async function deleteVoiceCue(projectId: string, cueId: string): Promise<void> {
  await request(`/api/projects/${projectId}/voice-cues/${cueId}`, { method: "DELETE" });
}

export async function generateVoiceCue(
  projectId: string,
  cueId: string,
  input: { speed?: number } = {},
): Promise<{ cue: VoiceCue; generationId: string }> {
  return request<{ cue: VoiceCue; generationId: string }>(
    `/api/projects/${projectId}/voice-cues/${cueId}/generate`,
    { method: "POST", body: input, retry: false },
  );
}

export async function selectVoiceCueTake(
  projectId: string,
  cueId: string,
  generationId: string,
): Promise<VoiceCue> {
  return request<VoiceCue>(`/api/projects/${projectId}/voice-cues/${cueId}/select`, {
    method: "POST",
    body: { generationId },
    retry: false,
  });
}

export async function createVoiceCueBatch(
  projectId: string,
  input: { sceneNodeId: string; cueIds: string[]; speed?: number },
): Promise<VoiceCueBatch> {
  return request<VoiceCueBatch>(`/api/projects/${projectId}/voice-cues/batch`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function getVoiceCueBatch(projectId: string, batchId: string): Promise<VoiceCueBatch> {
  return request<VoiceCueBatch>(
    `/api/projects/${projectId}/voice-cues/batch/${encodeURIComponent(batchId)}`,
  );
}

export async function cancelVoiceCueBatch(projectId: string, batchId: string): Promise<VoiceCueBatch> {
  return request<VoiceCueBatch>(
    `/api/projects/${projectId}/voice-cues/batch/${encodeURIComponent(batchId)}/cancel`,
    { method: "POST", retry: false },
  );
}

export async function retryVoiceCueBatch(projectId: string, batchId: string): Promise<VoiceCueBatch> {
  return request<VoiceCueBatch>(
    `/api/projects/${projectId}/voice-cues/batch/${encodeURIComponent(batchId)}/retry`,
    { method: "POST", retry: false },
  );
}

export async function suggestVoiceCasting(
  projectId: string,
  sceneNodeId: string,
): Promise<{ runId: string; proposal: AiProposal; assignments: VoiceCastAssignment[]; source: "llm" | "local" }> {
  return request(`/api/projects/${projectId}/voice-casting/suggest`, {
    method: "POST",
    body: { sceneNodeId },
    retry: false,
  });
}

/* ----------------------------- concept boards ----------------------------- */

export interface ConceptSourceSlotInput {
  shotKey: string;
  assetId: string;
}

export interface CreateConceptBoardInput {
  entityType: "character" | "location";
  entityId: string;
  kind: "character_sheet" | "location_board";
  styleId?: string;
  sourceMode?: "generate" | "use" | "stylize" | "import";
  sourceAssetIds?: string[];
  sourceSet?: ConceptSourceSlotInput[];
}

export async function getConceptBoards(projectId: string): Promise<ConceptBoard[]> {
  return request(`/api/projects/${projectId}/concept-boards`);
}

export async function getConceptBoard(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}`);
}

export async function createConceptBoard(
  projectId: string,
  input: CreateConceptBoardInput,
): Promise<ConceptBoard> {
  return request(`/api/projects/${projectId}/concept-boards`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function expandConceptBoard(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/expand`, { method: "POST", retry: false });
}

export async function generateConceptFaceAngles(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/face-angles`, { method: "POST", retry: false });
}

export async function replaceConceptBoardAnchor(
  boardId: string,
  input: { sourceMode: "generate" | "use" | "stylize"; sourceAssetIds?: string[]; styleId?: string },
): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/anchor`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function createConceptBoardElement(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/element`, { method: "POST", retry: false });
}

export async function approveConceptBoard(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/approve`, { method: "POST", retry: false });
}

export async function rejectConceptBoard(boardId: string, note: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/reject`, {
    method: "POST",
    body: { note },
    retry: false,
  });
}

export async function reopenConceptBoard(boardId: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/reopen`, { method: "POST", retry: false });
}

export async function regenerateConceptShot(boardId: string, shotKey: string): Promise<ConceptBoard> {
  return request(`/api/concept-boards/${boardId}/regenerate`, {
    method: "POST",
    body: { shotKey },
    retry: false,
  });
}

export async function deleteConceptBoard(boardId: string): Promise<void> {
  await request(`/api/concept-boards/${boardId}`, { method: "DELETE" });
}

/* ------------------------------ character looks ---------------------------- */

export interface CharacterLookAssetInput {
  assetId: string;
  role?: CharacterLookRole;
  note?: string;
}

export interface CreateCharacterLookInput {
  name: string;
  note?: string;
  promptTemplate?: string;
  negativePrompt?: string;
  assets?: CharacterLookAssetInput[];
}

export async function getCharacterLooks(
  projectId: string,
  characterId: string,
): Promise<CharacterLook[]> {
  return request(`/api/projects/${projectId}/characters/${characterId}/looks`);
}

export async function getCharacterLook(lookId: string): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}`);
}

export async function createCharacterLook(
  projectId: string,
  characterId: string,
  input: CreateCharacterLookInput,
): Promise<CharacterLook> {
  return request(`/api/projects/${projectId}/characters/${characterId}/looks`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function updateCharacterLook(
  lookId: string,
  input: {
    name?: string;
    note?: string;
    promptTemplate?: string;
    negativePrompt?: string;
    coverAssetId?: string | null;
  },
): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}`, { method: "PATCH", body: input });
}

export async function duplicateCharacterLook(lookId: string): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}/duplicate`, { method: "POST", retry: false });
}

export async function reorderCharacterLookAssets(lookId: string, assetIds: string[]): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}/order`, {
    method: "PATCH",
    body: { assetIds },
    retry: false,
  });
}

export interface ImportCharacterLookInput {
  name: string;
  note?: string;
  promptTemplate?: string;
  negativePrompt?: string;
  assets?: { assetId?: string; filename?: string; role?: CharacterLookRole; note?: string }[];
  coverAssetId?: string;
  coverFilename?: string;
}

export async function importCharacterLooks(
  projectId: string,
  characterId: string,
  looks: ImportCharacterLookInput[],
): Promise<{ created: CharacterLook[]; skipped: { name: string; reason: string }[] }> {
  return request(`/api/projects/${projectId}/characters/${characterId}/looks/import`, {
    method: "POST",
    body: { looks },
    retry: false,
  });
}

export async function deleteCharacterLook(lookId: string): Promise<void> {
  await request(`/api/character-looks/${lookId}`, { method: "DELETE" });
}

export async function addCharacterLookAsset(
  lookId: string,
  input: CharacterLookAssetInput,
): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}/assets`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export async function removeCharacterLookAsset(lookId: string, assetId: string): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}/assets/${assetId}`, { method: "DELETE" });
}

export async function attachGenerationToLook(
  lookId: string,
  input: { generationId: string; role?: CharacterLookRole; note?: string; makeCover?: boolean },
): Promise<CharacterLook> {
  return request(`/api/character-looks/${lookId}/from-generation`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

/* ------------------------------- production ------------------------------- */

export async function getProduction(
  projectId: string,
): Promise<{ tasks: ProductionTask[]; deliverables: Deliverable[] }> {
  return request(`/api/projects/${projectId}/production`);
}

/**
 * Writable task fields. Computed slices (assignee, prop, archived scene) are
 * server-owned and never travel back; propId is an explicit soft link.
 */
export type TaskWriteInput = Partial<
  Omit<ProductionTask, "assignee" | "prop" | "relatedSceneArchived">
>;

export async function createTask(
  projectId: string,
  input: TaskWriteInput & { title: string },
): Promise<ProductionTask> {
  return request(`/api/projects/${projectId}/production/tasks`, { method: "POST", body: input });
}

export async function updateTask(taskId: string, patch: TaskWriteInput): Promise<ProductionTask> {
  return request(`/api/production/tasks/${taskId}`, { method: "PATCH", body: patch });
}

export async function deleteTask(taskId: string): Promise<void> {
  await request(`/api/production/tasks/${taskId}`, { method: "DELETE" });
}

export async function createDeliverable(
  projectId: string,
  input: Partial<Deliverable> & { title: string },
): Promise<Deliverable> {
  return request(`/api/projects/${projectId}/production/deliverables`, { method: "POST", body: input });
}

export async function updateDeliverable(deliverableId: string, patch: Partial<Deliverable>): Promise<Deliverable> {
  return request(`/api/production/deliverables/${deliverableId}`, { method: "PATCH", body: patch });
}

/* --------------------------------- team ----------------------------------- */

export async function getTeam(projectId: string): Promise<{ members: ProjectMember[]; invites: ProjectInvite[] }> {
  return request(`/api/projects/${projectId}/members`);
}

export async function inviteMember(
  projectId: string,
  input: { email: string; role: ProjectRole },
): Promise<{ added: boolean; member?: ProjectMember; invite?: ProjectInvite; link?: string }> {
  return request(`/api/projects/${projectId}/invites`, { method: "POST", body: input });
}

export async function updateMemberRole(projectId: string, memberId: string, role: ProjectRole): Promise<void> {
  await request(`/api/projects/${projectId}/members/${memberId}`, { method: "PATCH", body: { role } });
}

export async function removeMember(projectId: string, memberId: string): Promise<void> {
  await request(`/api/projects/${projectId}/members/${memberId}`, { method: "DELETE" });
}

export async function acceptInvite(token: string): Promise<{ projectId: string }> {
  return request(`/api/invites/${token}/accept`, { method: "POST" });
}

export async function getOrgUsers(): Promise<User[]> {
  return request("/api/org/users");
}

/* ------------------------------ platform ---------------------------------- */

export interface WorkspaceAuditEntry {
  id: string;
  workspaceId: string | null;
  actorUserId: string | null;
  actorName?: string;
  action: string;
  targetType?: string;
  targetId?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export async function getWorkspaceMembers(workspaceId: string): Promise<{ members: WorkspaceMember[] }> {
  return request(`/api/workspaces/${workspaceId}/members`);
}

export async function getWorkspaceUsage(workspaceId: string): Promise<WorkspaceUsage> {
  return request(`/api/workspaces/${workspaceId}/usage`);
}

export async function updateWorkspace(workspaceId: string, patch: { name: string }): Promise<{ workspace: Workspace }> {
  return request(`/api/workspaces/${workspaceId}`, { method: "PATCH", body: patch });
}

export async function getWorkspaceInvites(workspaceId: string): Promise<{ invites: WorkspaceInvite[] }> {
  return request(`/api/workspaces/${workspaceId}/invites`);
}

export async function inviteWorkspaceMember(
  workspaceId: string,
  input: { email: string; role: Exclude<WorkspaceRole, "owner"> },
): Promise<{ invite: WorkspaceInvite; token: string; link: string }> {
  return request(`/api/workspaces/${workspaceId}/invites`, { method: "POST", body: input });
}

export async function revokeWorkspaceInvite(workspaceId: string, inviteId: string): Promise<{ invite: WorkspaceInvite }> {
  return request(`/api/workspaces/${workspaceId}/invites/${inviteId}/revoke`, { method: "POST" });
}

export async function updateWorkspaceMemberRole(
  workspaceId: string,
  userId: string,
  role: Exclude<WorkspaceRole, "owner">,
): Promise<{ member: WorkspaceMember }> {
  return request(`/api/workspaces/${workspaceId}/members/${userId}`, { method: "PATCH", body: { role } });
}

export async function removeWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
  await request(`/api/workspaces/${workspaceId}/members/${userId}`, { method: "DELETE" });
}

export async function transferWorkspaceOwnership(workspaceId: string, userId: string): Promise<void> {
  await request(`/api/workspaces/${workspaceId}/transfer-ownership`, { method: "POST", body: { userId } });
}

export async function getWorkspaceAudit(workspaceId: string): Promise<{ entries: WorkspaceAuditEntry[] }> {
  return request(`/api/workspaces/${workspaceId}/audit`);
}

export async function getBillingPlans(): Promise<{
  plans: BillingPlan[];
  provider: string;
  configured: boolean;
  currency: string;
}> {
  return request("/api/billing/plans");
}

export async function startBillingCheckout(input: {
  planCode: string;
  interval: "month" | "year";
}): Promise<BillingCheckoutSession> {
  return request("/api/billing/checkout", { method: "POST", body: input });
}

export async function completeMockBilling(sessionId: string): Promise<{
  workspace: Workspace;
  usage: WorkspaceUsage;
}> {
  return request("/api/billing/mock/complete", { method: "POST", body: { sessionId } });
}

export async function previewWorkspaceInvite(token: string): Promise<WorkspaceInvitePreview> {
  return request(`/api/workspace-invites/${token}`);
}

export async function acceptWorkspaceInvite(token: string): Promise<{
  workspaceId: string;
  role: WorkspaceRole;
  workspaceName: string;
  workspaceSlug: string;
}> {
  return request(`/api/workspace-invites/${token}/accept`, { method: "POST" });
}

export async function createWorkspace(input: {
  name: string;
  slug?: string;
  template?: "empty" | "film" | "series" | "audio";
}): Promise<{ workspace: Workspace; projectId: string | null; workspaces: { workspace: Workspace; role: WorkspaceRole }[] }> {
  return request("/api/workspaces", { method: "POST", body: input });
}

export async function getNotifications(): Promise<Notification[]> {
  return request("/api/notifications");
}

export async function markNotificationRead(id: string): Promise<void> {
  await request(`/api/notifications/${id}/read`, { method: "POST" });
}

export async function markAllNotificationsRead(): Promise<void> {
  await request("/api/notifications/read-all", { method: "POST" });
}

export async function globalSearch(q: string, projectId?: string): Promise<SearchResult[]> {
  const params = new URLSearchParams({ q });
  if (projectId) params.set("projectId", projectId);
  return request(`/api/search?${params.toString()}`);
}

export async function heartbeat(projectId: string, documentId: string, words: number, minutes: number): Promise<void> {
  if (!words && !minutes) return;
  await request("/api/writing/heartbeat", {
    method: "POST",
    body: { projectId, documentId, words, minutes },
  }).catch(() => undefined);
}

export async function getIntegrations(): Promise<Integration[]> {
  return request("/api/org/integrations");
}

export async function updateIntegration(
  integrationId: string,
  patch: { status?: string; config?: Record<string, string>; secret?: string | null },
): Promise<Integration> {
  return request(`/api/integrations/${integrationId}`, { method: "PATCH", body: patch });
}

export async function getApiTokens(): Promise<{ tokens: ApiToken[] }> {
  return request("/api/org/api-tokens");
}

export async function createApiToken(input: { name: string; scopes: string[]; expiresAt?: string }): Promise<{
  id: string;
  token: string;
  name: string;
  scopes: string[];
}> {
  return request("/api/org/api-tokens", { method: "POST", body: input });
}

export async function deleteApiToken(tokenId: string): Promise<void> {
  await request(`/api/org/api-tokens/${tokenId}`, { method: "DELETE" });
}

export async function downloadAudit(
  format: "csv" | "json",
  params: { from?: string; to?: string; projectId?: string } = {},
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const query = new URLSearchParams({ format });
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  if (params.projectId) query.set("projectId", params.projectId);
  const response = await fetch(apiUrl(`/api/org/audit?${query.toString()}`), {
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    credentials: "include",
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, data?.message ?? t("Не удалось экспортировать аудит"));
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = match ? decodeURIComponent(match[1]) : `korin-audit.${format}`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export async function getWebhooks(): Promise<{ webhooks: Webhook[]; events: string[] }> {
  return request("/api/org/webhooks");
}

export async function createWebhook(input: { url: string; events: string[]; secret?: string }): Promise<{
  webhook: Webhook;
  secret: string;
}> {
  return request("/api/org/webhooks", { method: "POST", body: input });
}

export async function deleteWebhook(id: string): Promise<void> {
  await request(`/api/org/webhooks/${id}`, { method: "DELETE" });
}

export async function getOrgOverview(): Promise<{
  org: import("@korin/core").Organization;
  users: number;
  projects: number;
  assets: number;
  words: number;
  storageBytes: number;
  storageHuman: string;
  readingTime: string;
}> {
  return request("/api/org/overview");
}

export async function updateMe(patch: Partial<Pick<User, "name" | "title" | "phone" | "avatarAssetId">>): Promise<User> {
  return request("/api/me", { method: "PATCH", body: patch });
}

export { API_URL };

export async function changePassword(current: string, next: string): Promise<void> {
  await request("/api/auth/change-password", { method: "POST", body: { current, next } });
}

export async function createOrgUser(input: {
  name: string;
  email: string;
  phone?: string;
  title?: string;
  role?: "owner" | "admin" | "member";
  password?: string;
}): Promise<{ user: User; temporaryPassword?: string }> {
  return request("/api/org/users", { method: "POST", body: input });
}
